import { useContext } from 'react';
import { ThemeProviderContext } from '../ThemeProviderContext';

/**
 * Access the provided theme name, which is useful for
 * propagating the theme class name to the children within
 * a Portal.
 * @returns string
 */

export function useThemeName() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.themeName;
}

/**
 * Access the provided theme brand name, which is useful for
 * propagating the brand class name to the children within
 * a Portal.
 * @returns string
 */

export function useBrandName() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.brandName;
}

/**
 * Access the provided theme custom brand, which is useful for
 * propagating the brand colors to the children within
 * a Portal.
 * @returns string
 */

export function useCustomBrand() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.customBrand;
}

/**
 * Access the provided theme custom brand name, which is useful for
 * propagating the theme colors to the children within
 * a Portal.
 * @returns string
 */

export function useAccentNeutralColors() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.accentNeutralColors;
}

/**
 * Access the provided theme accent neutral brand name, which is useful for
 * propagating the brand colors to the children within
 * a Portal.
 * @returns string
 */

export function useAccentNeutralBrand() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.accentNeutralBrand;
}

/**
 * Access the provided density value, which is useful for
 * propagating the density to the children within
 * a Portal.
 * @returns ThemeDensity
 */

export function useDensity() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.density;
}

/**
 * Access the provided sizing value, which is useful for
 * propagating the sizing to the children within
 * a Portal.
 * @returns ThemeSizing
 */

export function useSizing() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.sizing;
}

/**
 * Access the outermost `ThemeProvider`'s values.
 * Used for Portaled elements like Toasts that should
 * always use the outer app's theme.
 * @returns CoreThemeProviderValues
 */
export function useOutermostThemeProviderValues() {
  const themeContext = useContext(ThemeProviderContext);
  return themeContext?.outermostThemeProviderValues;
}
